import React, { useEffect, useState } from "react";
import {
  ChevronRight,
  Copy,
  CreditCard,
  Layers3,
  PackageCheck,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import { toast } from "react-toastify";
import API from "../utils/api";
import useAuth from "../hooks/useAuth";
import ConfirmDialog from "../components/ConfirmDialog";
import DisabledButtonWrapper from "../components/DisabledButtonWrapper";
import "./PayscalesPage.css";

const EQUIPMENT_LEVELS = [
  { value: "1", label: "Level 1", hint: "0%" },
  { value: "2", label: "Level 2", hint: "10%" },
  { value: "3", label: "Level 3", hint: "15%" },
  { value: "4", label: "Level 4", hint: "20%" },
  { value: "5", label: "Level 5", hint: "25%" },
  { value: "6", label: "Level 6", hint: "Custom" },
];

const CREDIT_LEVELS = ["A", "A+", "B", "B-", "D", "E", "COMM"];

const PAGE_STYLES = `
  .rps-page {
    --rps-bg: #f5f7fb;
    --rps-card: #ffffff;
    --rps-soft: #f8fafc;
    --rps-text: #172033;
    --rps-muted: #667085;
    --rps-border: #e6eaf0;
    --rps-blue: #2563eb;
    --rps-blue-soft: #eff6ff;
    --rps-green: #16845b;
    --rps-red: #c2414b;
    min-height: 100%;
    padding: 28px;
    background: var(--rps-bg);
    color: var(--rps-text);
    font-family: "DM Sans", Inter, system-ui, sans-serif;
  }
  .rps-page * { box-sizing: border-box; }
  .rps-shell { width: 100%; max-width: 1240px; margin: 0 auto; }
  .rps-header { display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; margin-bottom: 22px; }
  .rps-eyebrow { display: block; margin-bottom: 5px; color: var(--rps-blue); font-size: 12px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; }
  .rps-header h1 { margin: 0 0 6px; font-size: 28px; line-height: 1.15; letter-spacing: -.03em; }
  .rps-header p { max-width: 760px; margin: 0; color: var(--rps-muted); font-size: 14px; line-height: 1.55; }
  .rps-save { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 42px; padding: 10px 17px; border: 0; border-radius: 10px; background: var(--rps-blue); color: #fff; box-shadow: 0 7px 16px rgb(37 99 235 / 22%); font: inherit; font-size: 14px; font-weight: 700; cursor: pointer; white-space: nowrap; }
  .rps-save:disabled { cursor: not-allowed; opacity: .55; }
  .rps-layout { display: grid; grid-template-columns: 240px minmax(0, 1fr); gap: 20px; align-items: start; }
  .rps-sidebar { overflow: hidden; border: 1px solid var(--rps-border); border-radius: 14px; background: var(--rps-card); box-shadow: 0 8px 28px rgb(23 32 51 / 5%); }
  .rps-sidebar-head { padding: 16px; border-bottom: 1px solid var(--rps-border); }
  .rps-sidebar-head strong { display: block; font-size: 14px; }
  .rps-sidebar-head span { display: block; margin-top: 3px; color: var(--rps-muted); font-size: 12px; }
  .rps-new { display: inline-flex; align-items: center; justify-content: center; gap: 7px; width: 100%; min-height: 39px; margin-top: 13px; border: 1px solid #bfdbfe; border-radius: 9px; background: var(--rps-blue-soft); color: var(--rps-blue); font: inherit; font-size: 13px; font-weight: 700; cursor: pointer; }
  .rps-new:disabled { cursor: not-allowed; opacity: .5; }
  .rps-scale-list { padding: 7px; }
  .rps-scale-option { display: flex; align-items: center; justify-content: space-between; gap: 10px; width: 100%; padding: 11px 10px; border: 0; border-radius: 9px; background: transparent; color: var(--rps-text); font: inherit; text-align: left; cursor: pointer; }
  .rps-scale-option:hover { background: var(--rps-soft); }
  .rps-scale-option.is-selected { background: var(--rps-blue-soft); color: #1649aa; }
  .rps-scale-copy { min-width: 0; }
  .rps-scale-copy strong { display: block; overflow: hidden; font-size: 13px; font-weight: 700; text-overflow: ellipsis; white-space: nowrap; }
  .rps-scale-copy small { display: block; margin-top: 3px; color: var(--rps-muted); font-size: 11px; }
  .rps-scale-option.is-selected .rps-scale-copy small { color: #4b70b7; }
  .rps-scale-option svg { flex: 0 0 auto; color: var(--rps-muted); }
  .rps-main { display: grid; gap: 18px; min-width: 0; }
  .rps-card { overflow: hidden; border: 1px solid var(--rps-border); border-radius: 14px; background: var(--rps-card); box-shadow: 0 8px 28px rgb(23 32 51 / 5%); }
  .rps-card-head { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 17px 19px; border-bottom: 1px solid var(--rps-border); }
  .rps-title-group { display: flex; align-items: center; gap: 11px; min-width: 0; }
  .rps-section-icon { display: grid; width: 36px; height: 36px; flex: 0 0 auto; place-items: center; border-radius: 9px; background: var(--rps-blue-soft); color: var(--rps-blue); }
  .rps-card h2 { margin: 0; font-size: 16px; line-height: 1.25; letter-spacing: -.015em; }
  .rps-card-head p { margin: 3px 0 0; color: var(--rps-muted); font-size: 12px; line-height: 1.35; }
  .rps-card-body { padding: 19px; }
  .rps-fields { display: grid; grid-template-columns: minmax(0, 1.45fr) minmax(220px, .75fr); gap: 16px; }
  .rps-field label { display: block; margin-bottom: 7px; color: #344054; font-size: 12px; font-weight: 700; }
  .rps-field input, .rps-field select, .rps-table input, .rps-table select { width: 100%; min-height: 40px; border: 1px solid #d8dee8; border-radius: 9px; background: #fff; color: var(--rps-text); font: inherit; font-size: 14px; outline: none; transition: border-color .15s ease, box-shadow .15s ease; }
  .rps-field input, .rps-field select { padding: 9px 11px; }
  .rps-field input:focus, .rps-field select:focus, .rps-table input:focus, .rps-table select:focus { border-color: var(--rps-blue); box-shadow: 0 0 0 3px rgb(37 99 235 / 12%); }
  .rps-field input:disabled, .rps-field select:disabled, .rps-table input:disabled, .rps-table select:disabled { background: #f2f4f7; color: #98a2b3; }
  .rps-add { display: inline-flex; align-items: center; justify-content: center; gap: 7px; min-height: 36px; padding: 8px 12px; border: 1px solid #bfdbfe; border-radius: 9px; background: var(--rps-blue-soft); color: var(--rps-blue); font: inherit; font-size: 12px; font-weight: 700; cursor: pointer; white-space: nowrap; }
  .rps-add:disabled { cursor: not-allowed; opacity: .5; }
  .rps-table-scroll { width: 100%; overflow-x: auto; }
  .rps-table { width: 100%; min-width: 700px; margin: 0; border: 0; border-collapse: collapse; border-spacing: 0; }
  .rps-table th { padding: 11px 12px; border: 0; border-bottom: 1px solid var(--rps-border); background: var(--rps-soft); color: var(--rps-muted); font-size: 11px; font-weight: 700; letter-spacing: .045em; text-align: left; text-transform: uppercase; white-space: nowrap; }
  .rps-table td { padding: 11px 12px; border: 0; border-bottom: 1px solid var(--rps-border); background: #fff; color: var(--rps-text); font-size: 14px; vertical-align: middle; }
  .rps-table tbody tr:last-child td { border-bottom: 0; }
  .rps-table tbody tr:hover td { background: #fbfcfe; }
  .rps-table input, .rps-table select { min-width: 110px; min-height: 36px; padding: 7px 9px; }
  .rps-table .rps-credit-select { min-width: 105px; color: #1649aa; font-weight: 700; }
  .rps-table .rps-number { text-align: right; font-variant-numeric: tabular-nums; }
  .rps-table .rps-package-name { font-weight: 700; }
  .rps-table .rps-package-name small { display: block; margin-top: 3px; color: var(--rps-muted); font-size: 11px; font-weight: 400; }
  .rps-icon-button { display: grid; width: 34px; height: 34px; padding: 0; border: 0; border-radius: 8px; background: transparent; color: var(--rps-muted); cursor: pointer; place-items: center; }
  .rps-icon-button:hover { background: #fff1f2; color: var(--rps-red); }
  .rps-empty-row { padding: 28px !important; color: var(--rps-muted) !important; text-align: center; }
  .rps-empty { padding: 24px; border: 1px dashed #cfd6e2; border-radius: 12px; background: #fff; color: var(--rps-muted); text-align: center; }
  .rps-danger { display: flex; align-items: center; justify-content: space-between; gap: 18px; padding: 17px 19px; border: 1px solid #fecdd3; border-radius: 14px; background: #fffafb; }
  .rps-danger strong { display: block; color: #9f2733; font-size: 13px; }
  .rps-danger span { display: block; margin-top: 3px; color: #8d5960; font-size: 12px; }
  .rps-delete { display: inline-flex; align-items: center; justify-content: center; gap: 7px; min-height: 37px; padding: 8px 12px; border: 1px solid #fecdd3; border-radius: 9px; background: #fff; color: var(--rps-red); font: inherit; font-size: 12px; font-weight: 700; cursor: pointer; white-space: nowrap; }
  .rps-delete:disabled { cursor: not-allowed; opacity: .5; }
  @media (max-width: 900px) {
    .rps-page { padding: 18px; }
    .rps-layout { grid-template-columns: 1fr; }
    .rps-sidebar { position: static; }
    .rps-scale-list { display: flex; gap: 7px; overflow-x: auto; }
    .rps-scale-option { min-width: 180px; }
  }
  @media (max-width: 620px) {
    .rps-page { padding: 12px; }
    .rps-header { align-items: flex-start; }
    .rps-header h1 { font-size: 23px; }
    .rps-header p { display: none; }
    .rps-save { padding-inline: 12px; }
    .rps-save span { display: none; }
    .rps-fields { grid-template-columns: 1fr; }
    .rps-card-head { align-items: flex-start; padding: 15px; }
    .rps-card-body { padding: 15px; }
    .rps-danger { align-items: flex-start; flex-direction: column; }
  }
`;

function displayNumber(value) {
  if (value == null || value === "") return "";
  const number = Number(value);
  return Number.isFinite(number) ? String(number) : "";
}

function blankScale(packages) {
  return {
    id: null,
    name: "",
    equipmentLevel: "1",
    multiples: [],
    passthroughs: (packages || []).map((packageRow) => ({
      packageKey: packageRow.key,
      name: packageRow.name,
      amount: "",
    })),
  };
}

function scaleFromApi(scale, packages) {
  const amounts = Object.fromEntries(
    (scale.passthroughs || []).map((row) => [row.package_key, displayNumber(row.amount)])
  );
  const names = Object.fromEntries((packages || []).map((row) => [row.key, row.name]));
  const keys = packages?.length
    ? packages.map((row) => row.key)
    : (scale.passthroughs || []).map((row) => row.package_key);
  return {
    id: scale.id,
    name: scale.name || "",
    equipmentLevel: String(scale.equipment_level || 1),
    multiples: (scale.multiples || []).map((row) => ({
      creditLevel: row.credit_level || "",
      scoreFrom: String(row.score_from ?? ""),
      scoreTo: String(row.score_to ?? ""),
      multiple: displayNumber(row.multiple),
    })),
    passthroughs: keys.map((key) => ({
      packageKey: key,
      name: names[key] || key,
      amount: amounts[key] || "",
    })),
  };
}

function copyScaleName(name, scales) {
  const base = (name || "Pay scale").trim() || "Pay scale";
  const taken = new Set(
    (scales || []).map((scale) => String(scale.name || "").trim().toLowerCase())
  );
  const withinLimit = (value) => value.slice(0, 120);
  let candidate = withinLimit(`${base} copy`);
  if (!taken.has(candidate.toLowerCase())) return candidate;
  let number = 2;
  while (number < 100) {
    const suffix = ` copy ${number}`;
    candidate = withinLimit(`${base.slice(0, 120 - suffix.length).trim()}${suffix}`);
    if (!taken.has(candidate.toLowerCase())) return candidate;
    number += 1;
  }
  return withinLimit(`${base} copy`);
}

function payloadFromDraft(draft) {
  return {
    name: draft.name,
    equipment_level: Number(draft.equipmentLevel),
    multiples: draft.multiples.map((row) => ({
      credit_level: row.creditLevel,
      score_from: row.scoreFrom,
      score_to: row.scoreTo,
      multiple: row.multiple,
    })),
    passthroughs: draft.passthroughs.map((row) => ({
      package_key: row.packageKey,
      amount: row.amount === "" ? null : row.amount,
    })),
  };
}

export default function RepPayScalesPage() {
  const { isReadOnly } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [packages, setPackages] = useState([]);
  const [scales, setScales] = useState([]);
  const [draft, setDraft] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const applyList = (data, selectId) => {
    const nextPackages = data.packages || [];
    const nextScales = data.scales || [];
    setPackages(nextPackages);
    setScales(nextScales);
    const chosen = nextScales.find((scale) => scale.id === selectId) || nextScales[0];
    setDraft(chosen ? scaleFromApi(chosen, nextPackages) : null);
  };

  useEffect(() => {
    let cancelled = false;
    API.get("settings/rep-pay-scales/")
      .then((res) => {
        if (!cancelled) applyList(res.data);
      })
      .catch(() => {
        if (!cancelled) toast.error("Couldn't load rep pay scales.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectScale = (scale) => {
    setDraft(scaleFromApi(scale, packages));
  };

  const startNew = () => {
    setDraft(blankScale(packages));
  };

  const duplicateScale = () => {
    if (!draft) return;
    setDraft({
      id: null,
      name: copyScaleName(draft.name, scales),
      equipmentLevel: draft.equipmentLevel,
      multiples: draft.multiples.map((row) => ({ ...row })),
      passthroughs: draft.passthroughs.map((row) => ({ ...row })),
    });
    toast.success("Copy ready. Save it as a new pay scale.");
  };

  const updateMultiple = (index, changes) => {
    setDraft((current) => ({
      ...current,
      multiples: current.multiples.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...changes } : row
      ),
    }));
  };

  const updatePassthrough = (packageKey, amount) => {
    setDraft((current) => ({
      ...current,
      passthroughs: current.passthroughs.map((row) =>
        row.packageKey === packageKey ? { ...row, amount } : row
      ),
    }));
  };

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const body = payloadFromDraft(draft);
      const res = draft.id
        ? await API.put(`settings/rep-pay-scales/${draft.id}/`, body)
        : await API.post("settings/rep-pay-scales/", body);
      const list = await API.get("settings/rep-pay-scales/");
      applyList(list.data, res.data.id);
      toast.success("Rep pay scale saved.");
    } catch (err) {
      const data = err?.response?.data;
      const detail = data?.detail;
      const fieldMessage = data && typeof data === "object"
        ? Object.values(data).flat().find((value) => typeof value === "string")
        : null;
      toast.error(typeof detail === "string" ? detail : fieldMessage || "Couldn't save the pay scale.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!draft?.id) {
      setDraft(scales[0] ? scaleFromApi(scales[0], packages) : null);
      return;
    }
    setConfirmDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!draft?.id) return;
    setSaving(true);
    try {
      await API.delete(`settings/rep-pay-scales/${draft.id}/`);
      const list = await API.get("settings/rep-pay-scales/");
      applyList(list.data);
      toast.success("Rep pay scale deleted.");
    } catch {
      toast.error("Couldn't delete the pay scale.");
    } finally {
      setSaving(false);
    }
  };

  const levelLabel = EQUIPMENT_LEVELS.find((level) => level.value === String(draft?.equipmentLevel));

  return (
    <section className="rps-page" aria-labelledby="rep-pay-title">
      <style>{PAGE_STYLES}</style>
      <div className="rps-shell">
        <header className="rps-header">
          <div>
            <span className="rps-eyebrow">Commission Settings</span>
            <h1 id="rep-pay-title">Rep Pay Scales</h1>
            <p>
              Create commission structures using equipment levels, customer credit
              multipliers, and monthly ADT package passthroughs. Assign the completed
              pay scale from the rep’s user profile.
            </p>
          </div>
          <DisabledButtonWrapper
            isDisabled={isReadOnly || saving || loading || !draft}
            tooltipText={saving ? "Saving in progress..." : "Read-only mode: This action is disabled"}
          >
            <button
              type="button"
              className="rps-save"
              onClick={handleSave}
              disabled={isReadOnly || saving || loading || !draft}
            >
              <Save size={16} aria-hidden="true" />
              <span>{saving ? "Saving..." : "Save pay scale"}</span>
            </button>
          </DisabledButtonWrapper>
        </header>

        {loading ? (
          <div className="rps-empty">Loading pay scales…</div>
        ) : (
          <div className="rps-layout">
            <aside className="rps-sidebar" aria-label="Saved pay scales">
              <div className="rps-sidebar-head">
                <strong>Pay scales</strong>
                <span>{scales.length} saved {scales.length === 1 ? "scale" : "scales"}</span>
                <button type="button" className="rps-new" onClick={startNew} disabled={isReadOnly}>
                  <Plus size={15} aria-hidden="true" />
                  New pay scale
                </button>
              </div>
              <div className="rps-scale-list">
                {scales.length === 0 ? (
                  <div className="rps-empty">No pay scales yet.</div>
                ) : (
                  scales.map((scale) => (
                    <button
                      key={scale.id}
                      type="button"
                      className={`rps-scale-option${draft?.id === scale.id ? " is-selected" : ""}`}
                      onClick={() => selectScale(scale)}
                    >
                      <span className="rps-scale-copy">
                        <strong>{scale.name}</strong>
                        <small>
                          Equipment Level {scale.equipment_level}
                          {scale.equipment_level === 6 ? " · Custom" : ""}
                        </small>
                      </span>
                      <ChevronRight size={15} aria-hidden="true" />
                    </button>
                  ))
                )}
              </div>
            </aside>

            <main className="rps-main">
              {!draft ? (
                <div className="rps-empty">
                  Create a pay scale to configure commission calculations.
                </div>
              ) : (
                <>
                  <section className="rps-card" aria-labelledby="scale-details-title">
                    <header className="rps-card-head">
                      <div className="rps-title-group">
                        <span className="rps-section-icon">
                          <Layers3 size={18} aria-hidden="true" />
                        </span>
                        <div>
                          <h2 id="scale-details-title">Pay Scale Details</h2>
                          <p>Name this structure and choose its equipment deduction level.</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="rps-add"
                        onClick={duplicateScale}
                        disabled={isReadOnly || saving}
                      >
                        <Copy size={14} aria-hidden="true" />
                        Duplicate
                      </button>
                    </header>
                    <div className="rps-card-body">
                      <div className="rps-fields">
                        <div className="rps-field">
                          <label htmlFor="rep-pay-name">Pay scale name</label>
                          <input
                            id="rep-pay-name"
                            value={draft.name}
                            onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                            disabled={isReadOnly}
                            placeholder="Example: Experienced Rep"
                          />
                        </div>
                        <div className="rps-field">
                          <label htmlFor="rep-pay-level">Equipment level</label>
                          <select
                            id="rep-pay-level"
                            value={draft.equipmentLevel}
                            onChange={(event) => setDraft({ ...draft, equipmentLevel: event.target.value })}
                            disabled={isReadOnly}
                          >
                            {EQUIPMENT_LEVELS.map((level) => (
                              <option key={level.value} value={level.value}>
                                {level.label} · {level.hint}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="rps-card" aria-labelledby="credit-multipliers-title">
                    <header className="rps-card-head">
                      <div className="rps-title-group">
                        <span className="rps-section-icon">
                          <CreditCard size={18} aria-hidden="true" />
                        </span>
                        <div>
                          <h2 id="credit-multipliers-title">Credit Multipliers</h2>
                          <p>
                            Set the multiplier applied to each customer credit band.
                            {levelLabel ? ` Using ${levelLabel.label} equipment pricing.` : ""}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="rps-add"
                        disabled={isReadOnly}
                        onClick={() =>
                          setDraft({
                            ...draft,
                            multiples: [
                              ...draft.multiples,
                              { creditLevel: "", scoreFrom: "", scoreTo: "", multiple: "" },
                            ],
                          })
                        }
                      >
                        <Plus size={14} aria-hidden="true" />
                        Add row
                      </button>
                    </header>
                    <div className="rps-table-scroll">
                      <table className="rps-table">
                        <thead>
                          <tr>
                            <th>Credit level</th>
                            <th>Score from</th>
                            <th>Score to</th>
                            <th>Multiple</th>
                            <th><span className="sr-only">Actions</span></th>
                          </tr>
                        </thead>
                        <tbody>
                          {draft.multiples.length === 0 ? (
                            <tr>
                              <td className="rps-empty-row" colSpan={5}>
                                No credit multiplier rows yet. Select “Add row” to begin.
                              </td>
                            </tr>
                          ) : (
                            draft.multiples.map((row, index) => (
                              <tr key={`${draft.id || "new"}-${index}`}>
                                <td>
                                  <select
                                    className="rps-credit-select"
                                    aria-label={`Credit level for row ${index + 1}`}
                                    value={row.creditLevel || ""}
                                    disabled={isReadOnly}
                                    onChange={(event) =>
                                      updateMultiple(index, { creditLevel: event.target.value })
                                    }
                                  >
                                    <option value="" disabled>Select level</option>
                                    {CREDIT_LEVELS.map((level) => (
                                      <option key={level} value={level}>{level}</option>
                                    ))}
                                  </select>
                                </td>
                                <td>
                                  <input
                                    className="rps-number"
                                    aria-label={`Starting credit score for row ${index + 1}`}
                                    type="number"
                                    min="0"
                                    placeholder="0"
                                    value={row.scoreFrom}
                                    disabled={isReadOnly}
                                    onChange={(event) =>
                                      updateMultiple(index, { scoreFrom: event.target.value })
                                    }
                                  />
                                </td>
                                <td>
                                  <input
                                    className="rps-number"
                                    aria-label={`Ending credit score for row ${index + 1}`}
                                    type="number"
                                    min="0"
                                    placeholder="850"
                                    value={row.scoreTo}
                                    disabled={isReadOnly}
                                    onChange={(event) =>
                                      updateMultiple(index, { scoreTo: event.target.value })
                                    }
                                  />
                                </td>
                                <td>
                                  <input
                                    className="rps-number"
                                    aria-label={`Commission multiple for row ${index + 1}`}
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="0.00"
                                    value={row.multiple}
                                    disabled={isReadOnly}
                                    onChange={(event) =>
                                      updateMultiple(index, { multiple: event.target.value })
                                    }
                                  />
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="rps-icon-button"
                                    aria-label={`Remove credit multiplier row ${index + 1}`}
                                    disabled={isReadOnly}
                                    onClick={() =>
                                      setDraft({
                                        ...draft,
                                        multiples: draft.multiples.filter(
                                          (_, rowIndex) => rowIndex !== index
                                        ),
                                      })
                                    }
                                  >
                                    <Trash2 size={16} aria-hidden="true" />
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </section>

                  <section className="rps-card" aria-labelledby="passthrough-title">
                    <header className="rps-card-head">
                      <div className="rps-title-group">
                        <span className="rps-section-icon">
                          <PackageCheck size={18} aria-hidden="true" />
                        </span>
                        <div>
                          <h2 id="passthrough-title">ADT Package Passthroughs</h2>
                          <p>Monthly amount deducted from the package charge before the multiplier is applied.</p>
                        </div>
                      </div>
                    </header>
                    <div className="rps-table-scroll">
                      <table className="rps-table">
                        <thead>
                          <tr>
                            <th>ADT package</th>
                            <th>Monthly passthrough</th>
                          </tr>
                        </thead>
                        <tbody>
                          {draft.passthroughs.map((row) => (
                            <tr key={row.packageKey}>
                              <td className="rps-package-name">
                                {row.name}
                                <small>Deducted before commission calculation</small>
                              </td>
                              <td>
                                <input
                                  className="rps-number"
                                  aria-label={`${row.name} monthly passthrough`}
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={row.amount}
                                  disabled={isReadOnly}
                                  onChange={(event) =>
                                    updatePassthrough(row.packageKey, event.target.value)
                                  }
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>

                  <section className="rps-danger" aria-label="Delete pay scale">
                    <div>
                      <strong>Delete this pay scale</strong>
                      <span>This permanently removes the scale from your available commission structures.</span>
                    </div>
                    <button
                      type="button"
                      className="rps-delete"
                      onClick={handleDelete}
                      disabled={isReadOnly || saving}
                    >
                      <Trash2 size={15} aria-hidden="true" />
                      Delete pay scale
                    </button>
                  </section>
                </>
              )}
            </main>
          </div>
        )}
      </div>
      <ConfirmDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete pay scale"
        message="Are you sure you want to delete this pay scale? This action cannot be undone."
        itemName={draft?.name}
        confirmText="Delete"
        cancelText="Cancel"
        severity="error"
      />
    </section>
  );
}
